import { clear, createStore, del, entries, set } from 'idb-keyval';
import type { ClientCommand, CommandStatus } from '@ecobox/schema';
import { createStore as createObservable, type ReadableStore } from './store';

/**
 * The device ledger (02 §2.2): every command this device issued and its last known state, kept in
 * IndexedDB. The Firestore queue carries the data; the ledger makes sure nothing vanishes silently.
 */
export type LedgerState =
  'pending' | 'synced' | 'applied' | 'applied_with_exception' | 'rejected' | 'not_delivered';

export interface LedgerError {
  code: string;
  message: string;
}

export interface LedgerEntry {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  /** ISO time the command was made on this device. */
  createdAt: string;
  /** ISO time of the latest send attempt (first write or resend). */
  attemptedAt: string;
  updatedAt: string;
  state: LedgerState;
  serverStatus?: CommandStatus;
  lastError?: LedgerError;
  result?: Record<string, unknown>;
  /** A rejected command the person has acknowledged. */
  dismissed?: boolean;
  /** The exact envelope, so a resend writes the same command. */
  command: ClientCommand;
}

export const FINAL_STATES: readonly LedgerState[] = [
  'applied',
  'applied_with_exception',
  'rejected',
];

export function isFinalState(state: LedgerState): boolean {
  return FINAL_STATES.includes(state);
}

/** Commands whose effects the server has not (yet) confirmed: the overlay still applies them. */
export function isWaitingState(state: LedgerState): boolean {
  return !isFinalState(state);
}

/** Commands the server has not received: these block a safe sign-out. */
export function isUnsentState(state: LedgerState): boolean {
  return state === 'pending' || state === 'not_delivered';
}

/** Setting a key to `undefined` removes it from the entry. */
export type LedgerPatch = {
  [K in keyof Omit<LedgerEntry, 'id' | 'command'>]?: LedgerEntry[K] | undefined;
};

export interface Ledger extends ReadableStore<readonly LedgerEntry[]> {
  /** Resolves when entries saved by earlier sessions have been loaded. */
  readonly ready: Promise<void>;
  get(id: string): LedgerEntry | undefined;
  /** Memory is updated at once; the promise resolves when IndexedDB has the entry. */
  put(entry: LedgerEntry): Promise<void>;
  patch(id: string, patch: LedgerPatch, now?: Date): Promise<void>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
  /** Drop applied entries older than `maxAgeMs`, and dismissed rejected ones. */
  prune(maxAgeMs: number, now?: Date): Promise<number>;
  /** Wait for every queued IndexedDB write. */
  flush(): Promise<void>;
}

export interface LedgerOptions {
  dbName?: string;
}

function sortEntries(map: Map<string, LedgerEntry>): readonly LedgerEntry[] {
  return [...map.values()].sort((a, b) =>
    a.createdAt === b.createdAt ? a.id.localeCompare(b.id) : a.createdAt.localeCompare(b.createdAt),
  );
}

export function createLedger(options: LedgerOptions = {}): Ledger {
  const store = createStore(options.dbName ?? 'ecobox-ledger', 'commands');
  const map = new Map<string, LedgerEntry>();
  const snapshot = createObservable<readonly LedgerEntry[]>([]);
  let queue: Promise<void> = Promise.resolve();

  const publish = (): void => snapshot.set(sortEntries(map));

  const enqueue = (job: () => Promise<unknown>): Promise<void> => {
    const run = queue.then(job).then(
      () => undefined,
      (err: unknown) => {
        console.error('Device ledger write failed.', err);
      },
    );
    queue = run;
    return run;
  };

  const ready = (async () => {
    try {
      const saved = await entries<string, LedgerEntry>(store);
      for (const [id, entry] of saved) if (!map.has(id)) map.set(id, entry);
      publish();
    } catch (err) {
      console.error('Device ledger could not be loaded.', err);
    }
  })();

  return {
    ready,
    subscribe: snapshot.subscribe,
    getSnapshot: snapshot.getSnapshot,
    get: (id) => map.get(id),
    put(entry) {
      map.set(entry.id, entry);
      publish();
      return enqueue(() => set(entry.id, entry, store));
    },
    patch(id, patch, now = new Date()) {
      const current = map.get(id);
      if (!current) return Promise.resolve();
      const merged: Record<string, unknown> = {
        ...current,
        ...patch,
        updatedAt: now.toISOString(),
      };
      for (const key of Object.keys(merged)) if (merged[key] === undefined) delete merged[key];
      const next = merged as unknown as LedgerEntry;
      map.set(id, next);
      publish();
      return enqueue(() => set(id, next, store));
    },
    remove(id) {
      map.delete(id);
      publish();
      return enqueue(() => del(id, store));
    },
    clear() {
      map.clear();
      publish();
      return enqueue(() => clear(store));
    },
    async prune(maxAgeMs, now = new Date()) {
      const doomed = [...map.values()].filter(
        (e) =>
          (e.dismissed === true && e.state === 'rejected') ||
          ((e.state === 'applied' || e.state === 'applied_with_exception') &&
            now.getTime() - Date.parse(e.updatedAt) > maxAgeMs),
      );
      for (const e of doomed) map.delete(e.id);
      if (doomed.length > 0) publish();
      await enqueue(() => Promise.all(doomed.map((e) => del(e.id, store))));
      return doomed.length;
    },
    flush: () => queue,
  };
}
